/** Ngắt câu khi đã có tiếng rồi im hơn khoảng này. */
export const SILENCE_MS = 1000;

/** Chrome đóng phiên nhận giọng sau im lặng; chờ một nhịp rồi start lại. */
const RESTART_DELAY_MS = 200;

/**
 * Làm đẹp transcript từ Web Speech API: khoảng trắng, dấu câu, viết hoa đầu câu.
 * Không tự bịa dấu câu mới (engine trình duyệt không đảm bảo có dấu).
 * @param {string} raw
 * @param {string} [lang]
 * @returns {string}
 */
export function formatTranscript(raw, lang = 'en-US') {
  if (typeof raw !== 'string') return '';
  let text = raw.trim().replace(/\s+/g, ' ');
  if (!text) return '';

  // "hello ." / "hello ," → "hello." / "hello,"
  text = text.replace(/\s+([,.!?;:…])/g, '$1');
  // "hello.world" / "hello,world" → có khoảng sau dấu
  text = text.replace(/([,.!?;:…])(?!\s|$)/g, '$1 ');
  text = text.replace(/\s+/g, ' ').trim();

  const locale = lang || 'en-US';
  // Viết hoa ký tự đầu và sau . ! ? …
  text = text.replace(/(^|[.!?…]\s+)([^\s])/g, (_, prefix, ch) => (
    `${prefix}${ch.toLocaleUpperCase(locale)}`
  ));

  return text;
}

/**
 * Nhận giọng trên pre-join. Zoom Live Transcription chưa có vì user chưa vào phòng.
 * @param {object} options
 * @param {string} [options.lang]
 * @param {number} [options.silenceMs]
 * @param {(text: string) => void} [options.onPartial]
 * @param {(text: string) => void} options.onFinal
 * @param {(event: object) => void} [options.onError]
 * @param {typeof window.SpeechRecognition} [options.Recognition]
 * @returns {{ stop: (opts?: { send?: boolean }) => void }}
 */
export function startUtterance({
  lang = 'en-US',
  silenceMs = SILENCE_MS,
  onPartial,
  onFinal,
  onError,
  Recognition,
}) {
  const Ctor = Recognition
    || (typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition));
  if (!Ctor) {
    if (onError) onError({ error: 'unsupported' });
    return { stop() {} };
  }

  const recognition = new Ctor();
  recognition.lang = lang;
  recognition.continuous = true;
  recognition.interimResults = true;

  let transcript = '';
  let timer = null;
  let restartTimer = null;
  let finished = false;

  const clearRestart = () => {
    if (restartTimer) clearTimeout(restartTimer);
    restartTimer = null;
  };

  const finish = (shouldSend) => {
    if (finished) return;
    finished = true;
    if (timer) clearTimeout(timer);
    clearRestart();
    try {
      recognition.abort();
    } catch (err) {
      // already stopped
    }
    if (shouldSend && onFinal) onFinal(formatTranscript(transcript, lang));
  };

  const restart = () => {
    if (finished) return;
    try {
      recognition.start();
    } catch (err) {
      clearRestart();
      restartTimer = setTimeout(restart, RESTART_DELAY_MS);
    }
  };

  const armSilence = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => finish(true), silenceMs);
  };

  recognition.onresult = (event) => {
    let text = '';
    const results = event.results || [];
    for (let i = 0; i < results.length; i += 1) {
      text += results[i][0].transcript;
    }
    transcript = formatTranscript(text, lang);
    if (onPartial) onPartial(transcript);
    if (transcript) armSilence();
  };

  recognition.onerror = (event) => {
    const code = event && event.error;
    if (code === 'aborted' || code === 'no-speech') return;
    finish(false);
    if (onError) onError(event);
  };

  recognition.onend = () => {
    if (finished) return;
    if (transcript.trim()) {
      finish(true);
      return;
    }
    // no-speech đóng phiên dù user vẫn đang bật mic. Waveform dùng stream riêng nên vẫn chạy.
    clearRestart();
    restartTimer = setTimeout(restart, RESTART_DELAY_MS);
  };

  recognition.start();

  return {
    stop(opts) {
      finish(Boolean(opts && opts.send && transcript.trim()));
    },
  };
}
